const ASSISTANT_SAVED_FILE_SOURCES = new Set(['save_file', 'generate_image', 'return_file', 'returned_file']);

export const isAssistantSavedFile = (file) => (
  file?.file_type === 'saved' || ASSISTANT_SAVED_FILE_SOURCES.has(String(file?.source || ''))
);

const toSavedFileData = (file) => ({
  file_id: file.file_id,
  filename: file.filename,
  size_bytes: file.size_bytes,
  mime_type: file.mime_type,
  description: file.description || null,
  source: file.source || null,
  root_file_id: file.root_file_id || null,
  version_number: file.version_number || null,
  library_scope_type: file.library_scope_type || null,
  library_scope_id: file.library_scope_id ?? null,
  company_id: file.company_id ?? null,
});

const attachmentKey = (file) => `${file.message_id ?? ''}|${file.file_id}`;

// Files may be delivered on several replies. Deduplicate attachment occurrences,
// not library file identities, so returned files survive conversation reloads.
export function groupSavedFilesByMessage({ savedFiles = [], uploadedFiles = [] }) {
  const newestVersionByMessageRoot = new Map();
  const versionsByRoot = new Map();
  for (const file of uploadedFiles) {
    if (!file?.file_id) continue;
    const root = file.root_file_id || file.file_id;
    const key = `${file.message_id ?? ''}|${root}`;
    const version = file.version_number || 1;
    newestVersionByMessageRoot.set(key, Math.max(version, newestVersionByMessageRoot.get(key) || 0));
    if (!versionsByRoot.has(root)) versionsByRoot.set(root, new Map());
    versionsByRoot.get(root).set(file.file_id, version);
  }

  const supersededAttachments = new Set();
  const versionBadgeByFileId = new Map();
  for (const file of uploadedFiles) {
    if (!file?.file_id) continue;
    const root = file.root_file_id || file.file_id;
    const version = file.version_number || 1;
    if (version < newestVersionByMessageRoot.get(`${file.message_id ?? ''}|${root}`)) {
      supersededAttachments.add(attachmentKey(file));
    }
    const versions = versionsByRoot.get(root);
    if (versions.size > 1) {
      versionBadgeByFileId.set(file.file_id, {
        version,
        isLatest: version === Math.max(...versions.values()),
      });
    }
  }

  const savedFilesByMessageId = {};
  const savedFilesWithoutMessageId = [];
  const seenAttachments = new Set();
  for (const file of [...savedFiles, ...uploadedFiles.filter(isAssistantSavedFile)]) {
    if (!file?.file_id) continue;
    const key = attachmentKey(file);
    if (supersededAttachments.has(key) || seenAttachments.has(key)) continue;
    seenAttachments.add(key);
    const data = toSavedFileData(file);
    const badge = versionBadgeByFileId.get(file.file_id);
    if (badge) data.version_badge = badge;
    if (file.message_id != null) {
      if (!savedFilesByMessageId[file.message_id]) savedFilesByMessageId[file.message_id] = [];
      savedFilesByMessageId[file.message_id].push(data);
    } else {
      savedFilesWithoutMessageId.push(data);
    }
  }
  return { savedFilesByMessageId, savedFilesWithoutMessageId };
}
